import type { ProfileEducation, ProfileExit, ProfileResponse } from '@shared/schemas';

import { resolveCountryCode } from './countryMap';

/**
 * Format an ISO date string to dd/mm/yyyy (French locale).
 */
export function formatDateFr(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** "5 May 2026", for screens whose interface is in English, so is their date. */
export function formatDateEn(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

type ExitDatePrecision = NonNullable<ProfileExit['exit_date_precision']>;

/**
 * A client-reported exit date at the precision the client gave it (#654): a
 * "2024" is stored on the 1st of January and must not read back as 01/01/2024.
 * `2024`, `07/2024`, or dd/mm/yyyy. Read in UTC; the date is stored at UTC
 * midnight, and a local reading would slip to the 31st of December west of it.
 * A missing precision is a full date (rows written before #654).
 */
export function formatExitDateFr(iso: string, precision: ExitDatePrecision | null): string {
  const d = new Date(iso);
  const year = String(d.getUTCFullYear());
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  if (precision === 'year') return year;
  if (precision === 'month') return `${month}/${year}`;
  return `${String(d.getUTCDate()).padStart(2, '0')}/${month}/${year}`;
}

/** Same, for the English screens: `2024`, otherwise "Mar 2019" (a day is not shown there). */
export function formatExitDateEn(iso: string, precision: ExitDatePrecision | null): string {
  if (precision === 'year') return String(new Date(iso).getUTCFullYear());
  return new Date(iso).toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Format an ISO date string to dd/mm/yyyy HH:mm (French locale).
 */
export function formatDateTimeFr(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
}

/**
 * Format a large number into a compact string (e.g. 13.9K, 2.1M).
 */
export function fmtCount(n: number | null): string | null {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/**
 * "$1.05B", "€2.5M", "CHF 900": an exit amount in the currency the client's
 * file gave (#661), compact and to two decimals so the file's figure survives
 * (#685). Without one ("1.05B"), the file did not say which and no symbol is
 * guessed. Shared by the Exit Amount column and the profile page.
 */
export function compactAmount(n: number, currency: string | null = null): string {
  return new Intl.NumberFormat('en', {
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...(currency ? { style: 'currency', currency } : {}),
  }).format(n);
}

/** One currency's share of a profile's exit amounts; `currency` null for the amounts given without one. */
export interface ExitAmountTotal {
  currency: string | null;
  total: number;
}

/**
 * The sum of a profile's known exit amounts (#709), one total per currency:
 * nothing converts one into another, so "€2.5M + $1M" is all that can be said.
 * Codes in alphabetical order, the amounts without a currency last. `partial`
 * when an exit has no amount: the total then covers only the others.
 */
export function exitAmountTotals(exits: Pick<ProfileExit, 'exit_amount' | 'exit_currency'>[]): {
  totals: ExitAmountTotal[];
  partial: boolean;
} {
  const byCurrency = new Map<string | null, number>();
  for (const e of exits) {
    if (e.exit_amount == null) continue;
    byCurrency.set(e.exit_currency, (byCurrency.get(e.exit_currency) ?? 0) + e.exit_amount);
  }
  const totals = [...byCurrency]
    .map(([currency, total]) => ({ currency, total }))
    .sort((a, b) =>
      a.currency === b.currency ? 0 : a.currency === null ? 1 : b.currency === null ? -1 : a.currency.localeCompare(b.currency),
    );
  return { totals, partial: totals.length > 0 && exits.some((e) => e.exit_amount == null) };
}

/** "€2.5M + $1M", or null when no exit has an amount. */
export function formatExitTotals(totals: ExitAmountTotal[]): string | null {
  return totals.length ? totals.map((t) => compactAmount(t.total, t.currency)).join(' + ') : null;
}

/**
 * Convert a country name or 2-letter code to its flag emoji.
 * Handles full names like "France" as well as codes like "FR".
 */
export function countryFlag(input: string): string {
  const code = resolveCountryCode(input);
  if (!code) return '';
  return code
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');
}

/** Hoisted: building one of these per row is measurable, and it never varies. */
const REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });

/**
 * Spell a country out. `hq_country` arrives as either a name or a code, so a UI
 * that prints it raw shows "United Kingdom" for one company and "GB" for the
 * next. Unknown input is returned untouched; a country we cannot name is still
 * better shown as whatever LinkedIn called it than dropped.
 */
export function countryName(input: string): string {
  const code = resolveCountryCode(input);
  if (!code) return input;
  try {
    return REGION_NAMES.of(code) ?? input;
  } catch {
    return input;
  }
}

/**
 * Compute total career years from a profile's experiences.
 */
export function getCareerYears(profile: ProfileResponse): number {
  const starts = (profile.experiences ?? [])
    .map((e) => e.start_year)
    .filter((y): y is number => y !== null);
  if (starts.length === 0) return 0;
  return new Date().getFullYear() - Math.min(...starts);
}

/**
 * Get unique company names from a profile's experiences.
 */
export function getUniqueCompanies(profile: ProfileResponse): string[] {
  return [...new Set((profile.experiences ?? []).map((e) => e.company))];
}

// ── Experience grouping (LinkedIn-style) ──

export interface ExperienceItem {
  company: string;
  company_id?: string | null;
  company_logo_url: string | null;
  title: string;
  description: string | null;
  location: string | null;
  job_type: string | null;
  skills: string[];
  start_year: number | null;
  start_month: number | null;
  end_year: number | null;
  end_month: number | null;
  duration: string | null;
  is_current: boolean;
}

export interface ExperienceGroup {
  company: string;
  companyId: string | null;
  companyLogoUrl: string | null;
  totalDuration: string;
  startYear: number | null;
  endYear: number | null; // null = present
  roles: ExperienceItem[];
}

/**
 * Format a year range into a human-readable duration string.
 */
export function formatYearDuration(startYear: number | null, endYear: number | null): string {
  if (startYear === null) return '';
  const end = endYear ?? new Date().getFullYear();
  const years = end - startYear;
  if (years <= 0) return '< 1 yr';
  return years === 1 ? '1 yr' : `${years} yrs`;
}

/**
 * Group experiences by company name, compute total duration per company,
 * and return groups ordered by most recent start year (desc).
 * Companies with a single role are also returned as a group (with 1 role).
 */
export function groupExperiencesByCompany(experiences: ExperienceItem[]): ExperienceGroup[] {
  const map = new Map<string, ExperienceItem[]>();
  for (const exp of experiences) {
    const key = exp.company;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(exp);
  }

  const groups: ExperienceGroup[] = [];
  for (const [company, roles] of map) {
    // Sort roles within group by start_year desc
    roles.sort((a, b) => (b.start_year ?? 0) - (a.start_year ?? 0));

    const starts = roles.map((r) => r.start_year).filter((y): y is number => y !== null);
    const ends = roles.map((r) => r.end_year);
    const hasCurrent = roles.some((r) => r.is_current);
    const groupStart = starts.length > 0 ? Math.min(...starts) : null;
    const groupEnd = hasCurrent
      ? null
      : ends.every((y) => y !== null)
        ? Math.max(...(ends as number[]))
        : null;

    groups.push({
      company,
      companyId: roles.find((r) => r.company_id)?.company_id ?? null,
      companyLogoUrl: roles.find((r) => r.company_logo_url)?.company_logo_url ?? null,
      totalDuration:
        roles.length === 1 && roles[0].duration
          ? roles[0].duration
          : formatYearDuration(groupStart, groupEnd),
      startYear: groupStart,
      endYear: groupEnd,
      roles,
    });
  }

  // Sort groups by most recent start year desc
  groups.sort((a, b) => (b.startYear ?? 9999) - (a.startYear ?? 9999));
  return groups;
}

// ── Experience timeline (flat, newest first) ──

/**
 * Rank an experience on a single monthly scale, so two of them compare without
 * the caller dealing with the month being optional. A missing month sorts as
 * January; a missing year sorts last.
 */
function experienceStartRank(exp: ExperienceItem): number {
  if (exp.start_year === null) return Number.NEGATIVE_INFINITY;
  return exp.start_year * 12 + ((exp.start_month ?? 1) - 1);
}

/**
 * Flat career timeline, newest first, the ungrouped counterpart of
 * {@link groupExperiencesByCompany}, for the consolidated table column.
 *
 * Ongoing roles come first whatever their start date: a job still held reads as
 * more recent than any finished one, even one started later. Below them the
 * order is strictly reverse chronological, and undated experiences land at the
 * end rather than silently at the top.
 *
 * The API already returns experiences ordered by `startYear desc`, but that
 * order says nothing about these two cases and does not survive the JSON-blob
 * fallback in the backend mapper, so the ordering is settled here too.
 */
export function sortExperiencesDesc(experiences: ExperienceItem[]): ExperienceItem[] {
  return [...experiences].sort((a, b) => {
    if (a.is_current !== b.is_current) return a.is_current ? -1 : 1;
    return experienceStartRank(b) - experienceStartRank(a);
  });
}

/**
 * Year range of a single experience: "2019 – Present", "2015 – 2019", "2015",
 * or an empty string when nothing is dated.
 */
export function formatExperiencePeriod(exp: ExperienceItem): string {
  if (exp.start_year === null) return exp.is_current ? 'Present' : '';
  if (exp.is_current || exp.end_year === null) return `${exp.start_year} – Present`;
  if (exp.end_year === exp.start_year) return String(exp.start_year);
  return `${exp.start_year} – ${exp.end_year}`;
}

// ── Education (newest first) ──

/**
 * Degrees newest first, by start year, or by end year when only that one is
 * filled. Same start year: the later end first. Undated degrees go last, and
 * ties keep the API order. Shared by the profile page and the profile drawer
 * so both list them the same way (#681).
 */
export function sortEducationsDesc<T extends Pick<ProfileEducation, 'start_year' | 'end_year'>>(
  educations: T[],
): T[] {
  // 0 rather than -Infinity: below any real year, and two undated degrees
  // still compare to 0 instead of NaN.
  const rank = (y: number | null) => y ?? 0;
  return [...educations].sort(
    (a, b) =>
      rank(b.start_year ?? b.end_year) - rank(a.start_year ?? a.end_year) ||
      rank(b.end_year) - rank(a.end_year),
  );
}

/**
 * Year range of a degree: "2005 – 2009", or the one year that is filled.
 * Never "Present": LinkedIn has no "ongoing" flag on education, so a missing
 * end year only means the field was left empty (#681).
 */
export function formatEducationPeriod(edu: Pick<ProfileEducation, 'start_year' | 'end_year'>): string {
  if (edu.start_year != null && edu.end_year != null) return `${edu.start_year} – ${edu.end_year}`;
  return String(edu.start_year ?? edu.end_year ?? '');
}

/** First start year and closing year of a career; `null` when nothing is dated. */
function careerSpan(experiences: ExperienceItem[]) {
  const starts = experiences.map((e) => e.start_year).filter((y): y is number => y !== null);
  if (starts.length === 0) return null;
  const ongoing = experiences.some((e) => e.is_current || e.end_year === null);
  const ends = experiences.map((e) => e.end_year).filter((y): y is number => y !== null);
  const first = Math.min(...starts);
  const last = ongoing ? new Date().getFullYear() : ends.length > 0 ? Math.max(...ends) : null;
  return { first, last, ongoing };
}

/**
 * The three headline figures of a career: roles, companies, years of
 * experience. A figure that cannot be computed is `null` rather than 0, so the
 * caller leaves it out instead of claiming "0 years" (#677).
 *
 * Years run from the start of the first role to the end of the last one, or to
 * today when a role is ongoing, so overlapping roles cannot inflate them. The
 * rule is mirrored in SQL by the "Total Years of Experience" filter
 * (`findProfileIdsByExperienceSpan`, backend); change both together (#678).
 */
export function careerFigures(experiences: ExperienceItem[]): {
  roles: number | null;
  companies: number | null;
  years: number | null;
} {
  const companies = new Set(experiences.map((e) => e.company).filter(Boolean)).size;
  const span = careerSpan(experiences);
  const years =
    span && span.last !== null && span.last > span.first ? span.last - span.first : null;
  return {
    roles: experiences.length > 0 ? experiences.length : null,
    companies: companies > 0 ? companies : null,
    years,
  };
}

/**
 * One-line shape of a career, for the "Experience History" column and its
 * popover: "7 roles · 6 companies · 16 yrs · 2009 → present".
 *
 * Each part is dropped rather than faked when its data is missing, so a
 * profile with a single undated experience reads "1 role · 1 company" instead
 * of claiming 0 years since year zero. Returns an empty string for an empty
 * career, which the caller renders as nothing at all.
 */
export function summarizeCareer(experiences: ExperienceItem[]): string {
  const { roles, companies, years } = careerFigures(experiences);
  if (roles === null) return '';

  const parts = [`${roles} role${roles > 1 ? 's' : ''}`];
  if (companies !== null) parts.push(`${companies} compan${companies > 1 ? 'ies' : 'y'}`);
  if (years !== null) parts.push(`${years} yr${years > 1 ? 's' : ''}`);

  const span = careerSpan(experiences);
  if (span) parts.push(`${span.first} → ${span.ongoing ? 'present' : (span.last ?? span.first)}`);

  return parts.join(' · ');
}

/**
 * Format an ISO date string into a human-readable relative time string.
 * e.g. "just now", "5m ago", "3h ago", "2d ago", or falls back to dd/mm/yyyy.
 */
export function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB');
}
